import { useMemo, useState } from 'react';
import { ColDef, ColGroupDef, FirstDataRenderedEvent, GridColumnsChangedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, RowDataUpdatedEvent, SortChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react'
import { ISwapQualityIssueForList } from '@nkr/models/index';
import { SwapQualityIssueAction } from './swap-quality-issue-action';
import { SwapQualityIssueListType, SwapQualityIssueReportedBy } from '@nkr/enums/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import ActionButtonRenderer from './action-button-renderer';
import { Label } from 'react-bootstrap';

interface IProps {
    data: ISwapQualityIssueForList[],
    centerId: number,
    listType: SwapQualityIssueListType,
    reportedBy: SwapQualityIssueReportedBy,
    isAdmin: boolean,
    isAdminLogin: boolean,
    onConfirmReopen: (id: number) => void
}

const CommonList = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataUpdated = (e: RowDataUpdatedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const handleItemSelected = (id: number, action: any) => {
        if (action == SwapQualityIssueAction.Reopen) {
            props.onConfirmReopen(id);
        }
    };

    type ColType = ColDef | ColGroupDef;

    const cols: ColType[] = [
        {
            colId: 'id',
            field: 'id',
            headerName: 'ID',
            width: 40,
            cellClass: 'list-cell-first',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>ID</SortableHeaderComponent>,
        },
        {
            colId: 'swapInfo',
            field: 'swapInfo',
            headerName: 'swapInfo',
            width: 45,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Swap</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISwapQualityIssueForList;

                return (
                    <>{data.swapInfo != '' ? data.swapInfo : 'N/A'}</>
                );
            }
        },
        {
            colId: 'donor',
            headerName: 'Donor',
            headerClass: 'list-group-header-left',
            children: [
                {
                    colId: 'donorCenter',
                    field: 'donorCenter',
                    headerName: 'Donor Center',
                    width: 105,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as ISwapQualityIssueForList;

                        if (data.rdnCenterId > 0) {
                            return <>{data.donorCenter} <span style={{ color: 'red' }}>({data.rdnCenterAlias})</span></>;
                        }

                        return <>{data.donorCenter}</>;
                    }
                },
                {
                    colId: 'donorAlias',
                    field: 'donorAlias',
                    headerName: 'Donor Alias',
                    width: 75,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Alias</SortableHeaderComponent>,
                },
                {
                    colId: 'donorAbo',
                    field: 'donorAbo',
                    headerName: 'Donor ABO',
                    width: 35,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>ABO</SortableHeaderComponent>,
                },
                {
                    colId: 'donorAge',
                    field: 'donorAge',
                    headerName: 'Donor Age',
                    width: 35,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Age</SortableHeaderComponent>,
                }
            ]
        },
        {
            colId: 'recipient',
            headerName: 'Recipient',
            headerClass: 'list-group-header-not-left',
            children: [
                {
                    colId: 'recipientCenter',
                    field: 'recipientCenter',
                    headerName: 'Recipient Center',
                    width: 75,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
                },
                {
                    colId: 'recipientAlias',
                    field: 'recipientAlias',
                    headerName: 'Recipient Alias',
                    width: 75,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Alias</SortableHeaderComponent>,
                },
                {
                    colId: 'recipientAbo',
                    field: 'recipientAbo',
                    headerName: 'Recipient ABO',
                    width: 35,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>ABO</SortableHeaderComponent>,
                },
                {
                    colId: 'recipientAge',
                    field: 'recipientAge',
                    headerName: 'Recipient Age',
                    width: 35,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Age</SortableHeaderComponent>,
                },
                {
                    colId: 'recipientWaitTime',
                    field: 'recipientWaitTime',
                    headerName: 'Recipient Wait',
                    width: 35,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Wait</SortableHeaderComponent>,
                }
            ]
        },
        {
            colId: 'surgeon',
            headerName: 'Surgeon',
            headerClass: 'list-group-header-not-left',
            children: [
                {
                    colId: 'donorSurgeon',
                    field: 'donorSurgeon',
                    headerName: 'Donor Surgeon',
                    width: 95,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as ISwapQualityIssueForList;

                        return (
                            <>{data.donorSurgeon != '' ? data.donorSurgeon : <Label>not specified</Label>}</>
                        );
                    }
                },
                {
                    colId: 'recipientSurgeon',
                    field: 'recipientSurgeon',
                    headerName: 'Recipient Surgeon',
                    width: 95,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as ISwapQualityIssueForList;

                        return (
                            <>{data.recipientSurgeon != '' ? data.recipientSurgeon : <Label>not specified</Label>}</>
                        );
                    }
                }
            ]
        },
        {
            colId: 'shipmentMethod',
            field: 'shipmentMethod',
            headerName: 'Shipment Method',
            headerClass: 'list-header',
            width: 75,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Ship Method</SortableHeaderComponent>,
        },
        {
            colId: 'isFinalized',
            field: 'isFinalized',
            headerName: 'Finalized',
            headerClass: 'list-header',
            width: 50,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Finalized</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISwapQualityIssueForList;
                return <>{data.isFinalized ? 'Yes' : 'No'}</>
            }
        },
        {
            colId: 'actionMenu',
            cellClass: 'list-cell action-menu',
            headerClass: 'microsite-list-header',
            suppressSizeToFit: true,
            width: 100,
            cellRendererParams: { centerId: props.centerId, listType: props.listType, reportedBy: props.reportedBy, isAdmin: props.isAdmin, isAdminLogin: props.isAdminLogin, pullRight: true, onMenuToggle: handleMenuToggle, onItemSelected: handleItemSelected },
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>&nbsp;</SortableHeaderComponent>,
            cellRendererFramework: ActionButtonRenderer,
        }
    ]

    const columnDefs = useMemo(() => cols, [props.listType, props.reportedBy]);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <AgGridReact
            defaultColDef={{
                headerClass: 'list-header',
                cellClass: 'list-cell',
                sortable: true,
                resizable: false
            }}
            columnDefs={columnDefs}
            rowData={rowData}
            rowHeight={30}
            suppressContextMenu={true}
            suppressCellSelection={true}
            enableCellTextSelection={true}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
            onSortChanged={onSortChanged}
            onRowDataChanged={onRowDataChanged}
            onRowDataUpdated={onRowDataUpdated}
            onGridColumnsChanged={(e: GridColumnsChangedEvent) => e.api.sizeColumnsToFit()}
        />
    );
}

export default CommonList;